/* eslint-disable max-statements */
/* eslint-disable no-console */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  MILLISECONDS_IN_SECOND,
  WEBSOCKET_CONNECTION_CHECK_INTERVAL,
  WEBSOCKET_MAX_RECONNECT_DELAY_MS,
  WEBSOCKET_NORMAL_CLOSURE_CODE,
  WEBSOCKET_RECONNECT_BASE_FACTOR,
  WEBSOCKET_RECONNECT_MULTIPLIER_MS,
} from '@constants';
import { useSettings } from './useSettings';

interface WebSocketHook {
  sendMessage: (message: string) => void;
  closeConnection: () => void;
  onMessage: (name: string, callback: (message: string) => void) => void;
}

export const useWebSocket = (): WebSocketHook => {
  const { data: settings } = useSettings();
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [messageListeners, setMessageListeners] = useState<Map<string, (message: string) => void>>(new Map());
  const reconnectAttempts = useRef(0);
  const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false);

  const connect = useCallback(() => {
    if (!settings?.notificationsUrl || isConnectingRef.current) {
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setSocket((currentSocket) => {
      if (currentSocket?.readyState === WebSocket.OPEN) {
        return currentSocket;
      }

      if (currentSocket && currentSocket.readyState !== WebSocket.CLOSED) {
        currentSocket.close();
      }

      isConnectingRef.current = true;
      const ws = new WebSocket(settings.notificationsUrl);

      ws.onopen = () => {
        console.info('Connected to WebSocket');
        reconnectAttempts.current = 0;
        isConnectingRef.current = false;
      };

      ws.onmessage = (event) => {
        console.info('Received:', event.data);
        messageListeners.forEach((listener) => listener(event.data));
      };

      ws.onerror = (error) => {
        console.error('WebSocket Error:', error);
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        console.info('WebSocket Disconnected:', event?.reason);
        isConnectingRef.current = false;

        if (event.code !== WEBSOCKET_NORMAL_CLOSURE_CODE) {
          const delay = Math.min(
            WEBSOCKET_MAX_RECONNECT_DELAY_MS,
            WEBSOCKET_RECONNECT_BASE_FACTOR ** reconnectAttempts.current * WEBSOCKET_RECONNECT_MULTIPLIER_MS,
          ); // Exponential backoff (max 5s)
          console.info(`Reconnecting in ${delay / MILLISECONDS_IN_SECOND} sec...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
          reconnectAttempts.current++;
        }
      };

      return ws;
    });
  }, [settings?.notificationsUrl, messageListeners]);

  const checkConnection = useCallback(() => {
    if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
      connect();
    }
  }, [connect, socket]);

  useEffect(() => {
    connect();

    connectionCheckIntervalRef.current = setInterval(checkConnection, WEBSOCKET_CONNECTION_CHECK_INTERVAL);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConnection();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (socket) {
        socket.close(WEBSOCKET_NORMAL_CLOSURE_CODE);
      }
    };
  }, []);

  useEffect(() => {
    if (settings?.notificationsUrl && socket?.readyState !== WebSocket.OPEN) {
      connect();
    }
  }, [settings?.notificationsUrl]);

  const sendMessage = useCallback(
    (message: string) => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(message);
      } else {
        connect();
      }
    },
    [socket, connect],
  );

  const closeConnection = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    socket?.close(WEBSOCKET_NORMAL_CLOSURE_CODE);
  }, [socket]);

  const onMessage = useCallback((name: string, callback: (message: string) => void) => {
    setMessageListeners((prev) => {
      const updatedListeners = new Map(prev);
      updatedListeners.set(name, callback);
      return updatedListeners;
    });
  }, []);

  return { sendMessage, closeConnection, onMessage };
};
